import React, { useCallback, useImperativeHandle, useMemo, useRef, type Ref } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from 'styled-components';
import { Spinner } from '@aibuild/ui';
import type { AppHtmlPolicy, AppManifest, ViewSnapshot } from '@api/apps';
import HtmlSandboxViewer, { type HtmlSandboxError, type HtmlSandboxHandle } from '@components/HtmlSandboxViewer';
import { trackAction } from '@utils/datadog';
import { SUPPORTED_KIT_VERSIONS, loadKit, type KitContext } from '../../../appKit/host';
import { useHtmlPolicy } from '@hooks/useHtmlPolicy';
import { IDLE_LIVE } from '../useAppRunStream';
import { useSnapshotSource } from '../viewSnapshot';
import { bridgeStateOf } from './bridgeState';
import { useAppBridge } from './useAppBridge';
import { useTestRunDiagnostics } from './useTestRunDiagnostics';
import type { AppFramePage } from './appFramePage';
import type { AppFrameController } from './useAppRunController';
import { FrameBox, FrameNotice } from './AppFrame.styled';

// Tab-wide, so a remounted frame never sees a state older than one it already has.
let lastRev = 0;
const nextRev = (): number => ++lastRev;

export interface PreviewCapture {
  readonly image: Blob;
  readonly pageErrors: readonly string[];
}

export interface AppFramePreview {
  readonly pageKey: string;
  readonly loaded: boolean;
  capture(): Promise<PreviewCapture>;
}

interface AppFrameProps {
  manifest: AppManifest;
  page: AppFramePage;
  /** Null for a preview that runs nothing. */
  controller: AppFrameController | null;
  title: string;
  /** Mounted but out of sight, so it keeps its state while another pane shows. */
  hidden?: boolean;
  captureView?: () => Promise<ViewSnapshot | undefined>;
  onFixRequest?: (text: string) => void;
  fixLabel?: string;
  onOptions?: (values: Readonly<Record<string, unknown>>) => void;
  /** Handed in where the API cannot be asked for it: a visitor with no account. */
  policy?: AppHtmlPolicy;
  /** Another organization's app, opened through its public link. */
  untrusted?: boolean;
  previewRef?: Ref<AppFramePreview>;
}

const NoticeText: React.FC<{ id: string; message: string }> = ({ id, message }) => {
  const intl = useIntl();
  return <FrameNotice role="status">{intl.formatMessage({ id, defaultMessage: message })}</FrameNotice>;
};

const AppFrame: React.FC<AppFrameProps> = ({
  manifest,
  page,
  controller,
  title,
  hidden = false,
  captureView,
  onFixRequest,
  fixLabel,
  onOptions,
  policy: givenPolicy,
  untrusted = false,
  previewRef,
}) => {
  const intl = useIntl();
  const theme = useTheme() as unknown as Record<string, unknown>;
  const boxRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<HtmlSandboxHandle>(null);
  const kitVersion = manifest.presentation?.kitVersion ?? 1;
  const supported = SUPPORTED_KIT_VERSIONS.includes(kitVersion);
  const fetchedPolicy = useHtmlPolicy(givenPolicy === undefined);
  const policy = givenPolicy ?? fetchedPolicy.data;

  const kit = useQuery({
    queryKey: ['apps', 'kit', kitVersion],
    queryFn: async () => ({ version: kitVersion, source: await loadKit(kitVersion) }),
    enabled: supported,
    staleTime: Infinity,
    retry: false,
  });
  const source = useQuery({
    queryKey: ['apps', 'page', page.key],
    queryFn: page.load,
    enabled: supported,
    staleTime: Infinity,
    retry: false,
  });

  const run = controller?.run ?? null;
  const context = useMemo<KitContext>(
    () => ({
      theme,
      themeName: theme.themeName === 'dark' ? 'dark' : 'light',
      locale: intl.locale,
      readOnly: controller?.readOnly ?? false,
      test: Boolean(controller?.testRuns || run?.isTest),
      preview: controller === null,
      visitor: Boolean(controller?.requireAccount),
    }),
    [controller, intl.locale, run?.isTest, theme],
  );
  const live = controller?.live ?? IDLE_LIVE;
  const busy = Boolean(controller && (controller.isBusy || controller.submitting));
  const stopping = Boolean(controller?.stopping);
  const error = controller?.error ?? null;
  const { state, files } = useMemo(
    () =>
      bridgeStateOf(
        nextRev(),
        manifest,
        run,
        live,
        { busy, stopping, error: error ? { code: 'FAILED', message: error } : null },
        context,
      ),
    [manifest, run, live, busy, stopping, error, context],
  );

  const noCapture = useCallback(() => Promise.resolve(undefined), []);
  const bridge = useAppBridge({
    controller,
    state,
    files,
    captureView: captureView ?? noCapture,
    maxFileBytes: policy?.maxFeedFileBytes ?? 0,
    maxSessionBytes: policy?.maxFeedTotalBytes ?? 0,
  });

  const captureFrame = useCallback(() => viewerRef.current?.captureFrame() ?? null, []);
  useSnapshotSource(boxRef, 'HTML', captureFrame, !hidden);

  useImperativeHandle(
    previewRef,
    () => ({
      pageKey: page.key,
      get loaded() {
        return viewerRef.current?.loaded ?? false;
      },
      capture: async () => {
        const viewer = viewerRef.current;
        if (!viewer) throw new Error('The page has not loaded');
        const image = await viewer.captureFrame({ settle: true });
        return { image, pageErrors: (viewerRef.current ?? viewer).pageErrors.map((error) => error.message) };
      },
    }),
    [page.key],
  );

  const diagnostics = useTestRunDiagnostics({
    appKey: run?.appKey ?? manifest.key,
    runId: run?.runId ?? null,
    enabled: Boolean(run?.isTest) && !controller?.readOnly,
  });
  const reportDiagnostic = diagnostics.onError;
  const onError = useCallback(
    (reported: HtmlSandboxError) => {
      trackAction('app_html_page_error', {
        source: reported.source,
        kind: reported.kind,
        message: reported.message,
        appKey: manifest.key,
        appVersion: manifest.version,
        kitVersion,
      });
      reportDiagnostic(reported);
    },
    [kitVersion, manifest.key, manifest.version, reportDiagnostic],
  );

  if (!supported || source.error) {
    return (
      <FrameBox ref={boxRef}>
        <NoticeText id="apps.frame.needsUpdate" message="This app needs to be updated by its creator." />
      </FrameBox>
    );
  }
  if (kit.error || fetchedPolicy.error) {
    return (
      <FrameBox ref={boxRef}>
        <NoticeText id="apps.frame.reload" message="This app could not be loaded. Reload the page to try again." />
      </FrameBox>
    );
  }
  if (!kit.data || !source.data || !policy) {
    return (
      <FrameBox ref={boxRef}>
        <Spinner label="" />
      </FrameBox>
    );
  }
  return (
    <FrameBox ref={boxRef} data-testid="app-frame">
      <HtmlSandboxViewer
        html={source.data}
        policy={policy}
        kit={kit.data}
        agentFree={manifest.usesAgent === false}
        bridge={bridge}
        title={title}
        hidden={hidden}
        viewerRef={viewerRef}
        onError={onError}
        onRequestFailed={diagnostics.onRequestFailed}
        onNavigate={bridge.strike}
        onFixRequest={onFixRequest}
        fixLabel={fixLabel}
        onOptions={onOptions}
        untrusted={untrusted}
      />
    </FrameBox>
  );
};

export default AppFrame;
